
  import { defineComponent, PropType } from 'vue';
  import { ColumnHelper, createColumnHelper } from '@tanstack/vue-table';

  import TableAggregated from '@/common/components/redesigned/Table/TableAggregated.vue';
  import {
    TPaginationConfig,
    TTableAggregatedProgressBarOptions,
  } from '@/common/components/redesigned/Table/types/tableTypes';
  import text from '@/common/mixins/text';

  import { PAGINATION_CONFIG } from '../../constants/pagination';
  import { TABLE_AGGREGATED_PROGRESS_BAR_OPTIONS } from '../../constants/tableAggregatedProgress';
  import {
    FOREIGN_TRADE_BY_PRODUCTS_ACTIONS_NAME,
    FOREIGN_TRADE_BY_PRODUCTS_GETTERS_NAME,
    FOREIGN_TRADE_BY_PRODUCTS_MUTATIONS_NAME,
  } from '../../store/foreignTradeByProducts/name';
  import { IndicatorTypes } from '../../types/common-aggregated';
  import {
    TForeignTradeByProductsFilter,
    TForeignTradeByProductsItem,
    TForeignTradeByProductsTableData,
  } from '../../types/foreign-trade-by-products';
  import { getCodeCell, getProductNameCell } from '../../utils/table';

  export default defineComponent({
    name: 'ForeignTradeByProductsTable',
    components: {
      TableAggregated,
    },
    mixins: [text],
    props: {
      filter: {
        type: Object as PropType<TForeignTradeByProductsFilter>,
        required: true,
      },
    },
    data() {
      return {
        storePaths: {
          action: FOREIGN_TRADE_BY_PRODUCTS_ACTIONS_NAME.searchProductAggregated,
          getter: FOREIGN_TRADE_BY_PRODUCTS_GETTERS_NAME.getProductAggregated,
          mutation: FOREIGN_TRADE_BY_PRODUCTS_MUTATIONS_NAME.setProductAggregated,
        },
      };
    },
    computed: {
      getPaginationConfig(): TPaginationConfig {
        return PAGINATION_CONFIG;
      },
      getProgressBarOptions(): TTableAggregatedProgressBarOptions {
        return TABLE_AGGREGATED_PROGRESS_BAR_OPTIONS;
      },
    },
    methods: {
      generateColumns(data: TForeignTradeByProductsTableData) {
        if (!data) return [];

        const columnHelper = createColumnHelper<TForeignTradeByProductsItem>();
        // Поставил игнорирования из-за того, что в браузере вылезает ошибка типа (после обновления ts должна уйти)
        // @ts-ignore
        return [getCodeCell(columnHelper), getProductNameCell(columnHelper), ...this.getTableGroup(data, columnHelper)];
      },
      getTableGroup(data: TForeignTradeByProductsTableData, columnHelper: ColumnHelper<TForeignTradeByProductsItem>) {
        switch (this.filter.indicatorTypeId) {
          case IndicatorTypes.PRICE_AND_WEIGHT:
            return this.getTableGroupByPriceAndWeight(data, columnHelper);
          case IndicatorTypes.PRICE:
            return this.getTableGroupByPrice(data, columnHelper);
          case IndicatorTypes.WEIGHT:
            return this.getTableGroupByWeight(data, columnHelper);
        }
      },
      getTableGroupByPriceAndWeight(
        data: TForeignTradeByProductsTableData,
        columnHelper: ColumnHelper<TForeignTradeByProductsItem>,
      ) {
        return [
          ...data.periods.map((period: string, periodIndex: number) => {
            return columnHelper.group({
              header: period,
              id: `period-${periodIndex}`,
              columns: [
                columnHelper.accessor(
                  (row: TForeignTradeByProductsItem) => this.formatNumber(row.indicators[periodIndex]?.weight, 1),
                  {
                    header: 'Вес, тонн',
                    id: `first-weight-${periodIndex}`,
                    size: 160,
                  },
                ),
                columnHelper.accessor(
                  (row: TForeignTradeByProductsItem) => this.formatNumber(row.indicators[periodIndex]?.total.weight, 1),
                  {
                    header: 'Вес в % к итогу',
                    id: `first-weight-total-${periodIndex}`,
                    size: 160,
                  },
                ),
                columnHelper.accessor(
                  (row: TForeignTradeByProductsItem) => this.formatNumber(row.indicators[periodIndex]?.price, 1),
                  {
                    header: 'Стоимость, тыс. долл.',
                    id: `first-price-${periodIndex}`,
                    size: 160,
                  },
                ),
                columnHelper.accessor(
                  (row: TForeignTradeByProductsItem) => this.formatNumber(row.indicators[periodIndex]?.total.price, 1),
                  {
                    header: 'Стоимость в % к итогу',
                    id: `first-price-total-${periodIndex}`,
                    size: 160,
                  },
                ),
              ],
            });
          }),
        ];
      },
      getTableGroupByPrice(
        data: TForeignTradeByProductsTableData,
        columnHelper: ColumnHelper<TForeignTradeByProductsItem>,
      ) {
        return [
          columnHelper.group({
            header: 'Стоимость, тыс. долл.',
            columns: data.periods.map((period, periodIndex) => {
              return columnHelper.accessor(
                (row: TForeignTradeByProductsItem) => this.formatNumber(row.indicators[periodIndex].price, 1),
                {
                  header: period,
                  id: `second-price-${periodIndex}`,
                  size: 160,
                },
              );
            }),
          }),
          columnHelper.group({
            header: '% к итогу',
            columns: data.periods.map((period, periodIndex) => {
              return columnHelper.accessor(
                (row: TForeignTradeByProductsItem) => this.formatNumber(row.indicators[periodIndex].total.price, 1),
                {
                  header: period,
                  id: `second-price-total-${periodIndex}`,
                  size: 160,
                },
              );
            }),
          }),
        ];
      },
      getTableGroupByWeight(
        data: TForeignTradeByProductsTableData,
        columnHelper: ColumnHelper<TForeignTradeByProductsItem>,
      ) {
        return [
          columnHelper.group({
            header: 'Вес, тонн',
            columns: data.periods.map((period, periodIndex) => {
              return columnHelper.accessor(
                (row: TForeignTradeByProductsItem) => this.formatNumber(row.indicators[periodIndex].weight, 1),
                {
                  header: period,
                  id: `third-weight-${periodIndex}`,
                  size: 160,
                },
              );
            }),
          }),
          columnHelper.group({
            header: '% к итогу',
            columns: data.periods.map((period, periodIndex) => {
              return columnHelper.accessor(
                (row: TForeignTradeByProductsItem) => this.formatNumber(row.indicators[periodIndex].total.weight, 1),
                {
                  header: period,
                  id: `third-weight-total-${periodIndex}`,
                  size: 160,
                },
              );
            }),
          }),
        ];
      },
    },
  });
